import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Column } from '@gamma/ui/layout';
import { RouterLink } from '@gamma/ui/navigation';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import React, { useContext } from 'react';
import { useOutletContext, useParams } from 'react-router';
import { i18n } from '../../../shared/localization';
import { AuthContext } from '../../../shared/providers/AuthProvider';
import { DetailsGrid, DetailsGridRow } from '../../detections/components';
import { OutletContextType } from './Integrations';
import {
  AvailabilityTag,
  IntegrationsConfigInfo,
} from './IntegrationsConfigCard';
import {
  emptyIntegrationsData,
  IntegrationsConstants,
  IntegrationsData,
} from './IntegrationsConstants';

const { integrations } = i18n.pages;
const { learnMore, configure, overview } = integrations;

export const IntegrationsDetailPage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { integration } = useParams();
  const { userLicense, userRole, userPrivileges } = useContext(AuthContext);
  const { configsLoading } = useOutletContext<OutletContextType>();
  const { tenant = '' } = userLicense ?? {};

  const integrationData: IntegrationsData = integration
    ? IntegrationsConstants[integration]
    : emptyIntegrationsData;

  const {
    title,
    description,
    pathName,
    privilegeName,
    settingsModal,
    learnMoreLink,
    overviewContent,
    tags,
    fallbackText,
    tableInfo,
  } = integrationData;

  const isEnabled = userPrivileges?.[tenant ?? '']?.[privilegeName] ?? false;

  const bgColor = useColorModeValue('gray.100', 'gray.800');
  return configsLoading ? (
    <GraphQLReqStatus loading={configsLoading}></GraphQLReqStatus>
  ) : (
    <VStack>
      <Column w="1200px" margin="auto" flex="auto">
        <VStack spacing={4} alignItems="start">
          {settingsModal &&
            settingsModal({
              title,
              onOpen,
              isOpen,
              onClose,
            })}
          <HStack>
            <Box width="400px" alignItems="flex-start">
              <IntegrationsConfigInfo
                availabilityTag={
                  <AvailabilityTag
                    isEnabled={isEnabled}
                    fallbackText={fallbackText}
                  />
                }
                description={description}
                title={title}
                pathName={pathName}
                tags={tags}
                isLoading={configsLoading}
                footer={
                  <ButtonGroup width="100%">
                    {settingsModal && (
                      <Button
                        variant="solid"
                        colorScheme="blue"
                        height="48px"
                        width="120px"
                        onClick={onOpen}
                        isDisabled={
                          userRole === 'viewer' || userRole === 'analyst'
                        }
                      >
                        {configure}
                      </Button>
                    )}
                    {learnMoreLink && (
                      <Button
                        as={RouterLink}
                        target="_blank"
                        variant="solid"
                        colorScheme="gray"
                        height="48px"
                        width="120px"
                        to={`${learnMoreLink}`}
                      >
                        {learnMore}
                      </Button>
                    )}
                  </ButtonGroup>
                }
              />
            </Box>
            <Box
              bgImage={`/assets/images/integrations/${pathName}-image.jpg`}
              bgSize="cover"
              h="394px"
              w="700px"
            ></Box>
          </HStack>
        </VStack>
      </Column>
      <Column
        w="100vw"
        bg={bgColor}
        marginLeft="-3"
        maxW="100vw"
        justifyItems="center"
      >
        <VStack
          w="1248px"
          spacing={2}
          alignItems="start"
          paddingLeft="50px"
          margin="auto"
          flex="auto"
        >
          {overviewContent && (
            <Box maxW="1100px">
              <Text textStyle="h4" data-testid="integration-overview-label">
                {overview}
              </Text>
              <Text data-testid="integration-overview-value">
                {overviewContent}
              </Text>
            </Box>
          )}
          {tableInfo && (
            <DetailsGrid
              marginLeft={2}
              marginTop={4}
              gridTemplateColumns="200px 1fr"
            >
              {tableInfo.map(({ header, value }) => {
                const key = header.split(' ').join('-').toLowerCase();
                return (
                  <DetailsGridRow
                    title={
                      <HStack color="gray.300">
                        <Text>{header}</Text>
                      </HStack>
                    }
                    key={key}
                    data-testid={`integration-${key}`}
                  >
                    {Array.isArray(value)
                      ? value.map((valueItem, i) => (
                          <React.Fragment key={`${header}-${i}`}>
                            {Boolean(i) && <br />}
                            {valueItem}
                          </React.Fragment>
                        ))
                      : value}
                  </DetailsGridRow>
                );
              })}
            </DetailsGrid>
          )}
        </VStack>
      </Column>
    </VStack>
  );
};
